/**
 * Iter-346 — Admin Dashboard Discrepancy Report.
 *
 * Surfaces "count says N, records say M" mismatches in one clickable
 * screen so Operations can self-serve investigations without eng.
 *
 * Every category shows: severity light, title, count, first few
 * sample rows, and a `Drill down` link that navigates to the pre-
 * filtered list where the operator can act.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, AlertTriangle, CheckCircle2, ArrowRight,
  ShieldAlert, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const SEV_COLOR = { red: 'bg-rose-500', amber: 'bg-amber-500', green: 'bg-emerald-500', unknown: 'bg-slate-400' };
const SEV_LABEL = { red: 'Action needed', amber: 'Review', green: 'OK', unknown: 'Unknown' };
const SEV_BADGE = {
  red: 'bg-rose-50 border-rose-200 text-rose-800',
  amber: 'bg-amber-50 border-amber-200 text-amber-900',
  green: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  unknown: 'bg-slate-50 border-slate-200 text-slate-700',
};

export default function AdminDiscrepancies() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/discrepancies`, AUTH());
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load discrepancy report');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="p-6 flex items-center gap-3 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading discrepancy report…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4" data-testid="admin-discrepancies-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">Ops · Data Integrity</div>
          <h1 className="text-2xl font-bold" data-testid="admin-discrepancies-title">
            Discrepancy Report
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            "Count says N, records say M" — every mismatch is clickable.
          </p>
        </div>
        <Button
          variant="outline" size="sm" onClick={load}
          data-testid="admin-discrepancies-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Overall roll-up */}
      <Card className={`p-4 border ${SEV_BADGE[data.overall]}`} data-testid="admin-discrepancies-overall">
        <div className="flex items-center gap-3">
          <span className={`w-3 h-3 rounded-full ${SEV_COLOR[data.overall]}`} />
          <div className="flex-1">
            <div className="font-semibold">
              {data.overall === 'green'
                ? 'All detectors clean'
                : data.overall === 'amber'
                  ? `${data.summary.amber} categor${data.summary.amber === 1 ? 'y' : 'ies'} to review`
                  : `${data.summary.red} action-needed · ${data.summary.amber} review`}
            </div>
            <div className="text-xs opacity-70">
              {data.summary.total_categories} detectors ·
              generated {new Date(data.generated_at).toLocaleString()}
            </div>
          </div>
        </div>
      </Card>

      {/* Category list */}
      <div className="space-y-2">
        {data.categories.map((c) => {
          const hasCount = (c.count ?? 0) > 0;
          const sev = c.severity in SEV_COLOR ? c.severity : 'unknown';
          const isOpen = !!expanded[c.code];
          return (
            <Card
              key={c.code}
              className={`p-3 border ${hasCount ? SEV_BADGE[sev] : 'bg-white border-slate-200'}`}
              data-testid={`discrepancy-row-${c.code}`}
            >
              <button
                type="button"
                className="w-full flex items-start justify-between gap-3 text-left"
                onClick={() => setExpanded((e) => ({ ...e, [c.code]: !isOpen }))}
                data-testid={`discrepancy-toggle-${c.code}`}
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <span className={`mt-1 w-2.5 h-2.5 rounded-full ${SEV_COLOR[sev]} shrink-0`} />
                  <div className="min-w-0">
                    <div className="font-semibold flex items-center gap-2">
                      {sev === 'red' && <AlertTriangle className="w-3.5 h-3.5" />}
                      {sev === 'green' && <CheckCircle2 className="w-3.5 h-3.5" />}
                      {sev === 'unknown' && <ShieldAlert className="w-3.5 h-3.5" />}
                      <span className="truncate">{c.title}</span>
                    </div>
                    <div className="text-xs opacity-70 mt-0.5 flex flex-wrap gap-x-3">
                      <span>{c.code}</span>
                      {c.note && <span className="italic">{c.note}</span>}
                      {c.error && <span className="text-rose-700">detector error: {c.error}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xl font-bold tabular-nums" data-testid={`discrepancy-count-${c.code}`}>
                    {c.count ?? '—'}
                  </span>
                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </button>

              {isOpen && (
                <div className="mt-3 pt-3 border-t border-current/10 space-y-2">
                  {c.drill_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(c.drill_url)}
                      data-testid={`discrepancy-drill-${c.code}`}
                    >
                      Drill down to affected records
                      <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  )}
                  {c.sample && c.sample.length > 0 ? (
                    <div className="text-xs overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left opacity-60">
                            {Object.keys(c.sample[0]).map((k) => (
                              <th key={k} className="pr-3 pb-1 font-medium">{k}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {c.sample.map((row, i) => (
                            <tr key={i} className="border-t border-current/10">
                              {Object.values(row).map((v, j) => (
                                <td key={j} className="pr-3 py-1 font-mono text-[11px] truncate max-w-[220px]">
                                  {v == null ? '—' : String(v)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-xs opacity-70">No affected records — this detector is clean.</div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
